import { VoidCallback } from '@deqo/foundation';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { waitFor } from '../../../CodesThatsGottaGoSomewhereElse';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { Icon } from '../../../concepts/resources/Icon';
import { Menu } from '../../../ui';
import { DropButton } from '../../../ui/DropButton';
import { Skeleton } from '../../../ui/Skeleton';
import { useResolve } from '../../Container';
import { useOnCloneBoard, useOnCopyBoardLink, useOnDeleteBoard, useOnMoveBoard, useOnRenameBoard } from '../../actions/BoardActions';
import { Popover } from '../../canvas/ui/Popover';
import { Flexer } from '../elements/LayoutElements';

const useBoardAndPermissionsQuery = (boardId: string) => {
    const api = useResolve(ApiClient);
    return useQuery({
        queryKey: ['me.permissions', boardId],
        cacheTime: 60 * 1000,
        queryFn: async () => {
            // Make sure it takes at least 200ms to allow the animation
            const p = api.exec(v2.me.permissions(['board', boardId]));
            const b = api.exec(v2.boards.info(boardId));
            await waitFor(200);
            return {
                permission: await p,
                board: await b,
            };
        },
    });
};

const BoardActionMenuSkeleton = () =>
    <Flexer minWidth="12">
        <Menu>
            <Menu.Item>
                <Skeleton />
            </Menu.Item>
            <Menu.Divide />
            <Menu.Item>
                <Skeleton />
            </Menu.Item>
            <Menu.Item>
                <Skeleton />
            </Menu.Item>
            <Menu.Item>
                <Skeleton />
            </Menu.Item>
            <Menu.Item>
                <Skeleton />
            </Menu.Item>
        </Menu>
    </Flexer>;

const BoardActionMenu = React.memo<{ boardId: string, onClose: VoidCallback }>(({ boardId, onClose }) => {

    const { isLoading, data } = useBoardAndPermissionsQuery(boardId);
    const { permission, board } = data ?? {};
    const canWrite = permission?.includes('wo:write');

    const onCopyLink = useOnCopyBoardLink(onClose);
    const onRename = useOnRenameBoard(onClose);
    const onMove = useOnMoveBoard(onClose);
    const onClone = useOnCloneBoard(onClose);
    const onDelete = useOnDeleteBoard(onClose);

    if (isLoading) {
        return <BoardActionMenuSkeleton />;
    }

    return <Flexer minWidth="12">
        <Menu>
            <Menu.Item onClick={() => onCopyLink(board.id)}>
                <Menu.ItemText>Copy Link</Menu.ItemText>
            </Menu.Item>
            <Menu.Divide />
            <Menu.Item disabled={!canWrite} onClick={() => onRename(board)}>
                <Menu.ItemText>Rename</Menu.ItemText>
            </Menu.Item>
            <Menu.Item disabled={!canWrite && !!board.workspaceId} onClick={() => onMove(board)}>
                <Menu.ItemText>Move</Menu.ItemText>
            </Menu.Item>
            <Menu.Item disabled={!canWrite} onClick={() => onClone(board)}>
                <Menu.ItemText>Clone</Menu.ItemText>
            </Menu.Item>
            <Menu.Item disabled={!canWrite} onClick={() => onDelete(board)}>
                <Menu.ItemText>Delete</Menu.ItemText>
            </Menu.Item>
        </Menu>
    </Flexer>;
});

export const NewBoardActionMenuButton = React.memo<{ boardId: string }>(({ boardId }) => {

    const [open, setOpen] = useState(false);

    return <Flexer margin="0 .5">
        <Popover
            open={open}
            onOpenChange={setOpen}
            placement="bottom-end"
            inner={{ direction: 'down' }}
            content={open ? <BoardActionMenu boardId={boardId} onClose={() => setOpen(false)} /> : <BoardActionMenuSkeleton />}
        >
            <DropButton active={open}>
                <Icon size="1.5" src="deqo://icons/hero/dots-horizontal" />
            </DropButton>
        </Popover>
    </Flexer>;

});
